import type { FirebaseApp } from "@firebase/app";
import { getMessaging, getToken, isSupported } from "@firebase/messaging";
import { useAsync, useMountEffect } from "@react-hookz/web";
import { useCallback, useEffect, useMemo } from "react";

import { useDeviceDetect } from "shared/hooks/device-detect";

export const useIsNotificationSupported = () => {
  const { isFirefox } = useDeviceDetect();
  const [state, actions] = useAsync(async () => {
    const isNotificationApiAvailable =
      globalThis !== undefined && globalThis.Notification !== undefined;
    const isSupportFirebase = await isSupported();
    return !isFirefox && isNotificationApiAvailable && isSupportFirebase;
  });

  useMountEffect(actions.execute);
  return { isNotificationSupported: state };
};

export const useFirebaseMessaging = (app?: FirebaseApp) => {
  const { isNotificationSupported } = useIsNotificationSupported();

  const messaging = useMemo(() => {
    if (isNotificationSupported.result !== true) return;
    if (!app) return;
    return getMessaging(app);
  }, [app, isNotificationSupported.result]);

  return messaging;
};

export const useFirebaseRegistrationToken = (app?: FirebaseApp) => {
  const messaging = useFirebaseMessaging(app);

  const [state, actions] = useAsync(async () => {
    if (!messaging) return;

    const registration = await navigator.serviceWorker.ready;
    return getToken(messaging, {
      serviceWorkerRegistration: registration,
    });
  });

  useEffect(() => {
    if (!messaging) return;
    void actions.execute();
  }, [actions, messaging]);

  const refresh = useCallback(() => {
    actions.reset();
    return actions.execute();
  }, [actions]);

  return {
    refreshRegistrationToken: refresh,
    registrationToken: state,
  };
};
